import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import {
  playableBlockchainRarities,
  scarcityNames,
} from '@sorare/core/src/lib/cards';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import type { Unit } from '@sorare/core/src/lib/style';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PickerCard } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';

const Grid = styled(Horizontal).attrs({ gap: 1, center: true })`
  flex-wrap: wrap;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-width: 460px;
  }
`;

type Props = {
  rarities?: readonly Rarity[];
  selected: Rarity;
  onSelect: (rarity: Rarity) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

const SIZE_TO_ICON_SIZE: Record<
  NonNullable<ComponentProps<typeof ShieldButton>['size']>,
  Unit
> = {
  xxs: 1.5,
  xs: 2,
  sm: 3,
  md: 4,
  lg: 4,
  xl: 5,
};

export const RarityPicker = ({
  rarities = playableBlockchainRarities,
  selected,
  onSelect,
  size = 'md',
}: Props) => {
  if (rarities.length <= 1) {
    return null;
  }

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          aria-label={scarcityNames[selected]}
          size={size}
          icon={
            <ScarcityIcon rarity={selected} size={SIZE_TO_ICON_SIZE[size]} />
          }
          orientation="down"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose Rarity" />
          </PickerHeader>
          <Grid>
            {rarities.map(rarity => (
              <PickerCard
                key={rarity}
                selected={rarity === selected}
                background={getRarityColor(rarity, { opacity: 0.2 })}
                onClick={() => {
                  onSelect(rarity);
                  closeDropdown();
                }}
              >
                <ScarcityIcon rarity={rarity} size={6} />
              </PickerCard>
            ))}
          </Grid>
        </>
      )}
    </Dropdown>
  );
};
